import {Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography} from '@mui/material';
import {DEFAULT_FIXED_QUALITY_GRADING} from 'appConstants';
import {type FishGroup, WriteQualityModelAttributeAssignmentTypeEnum} from 'client';
import {FishGroupQualityModal} from 'components/FishGroups/FishGroupsQualityModal';
import {useModalWindow} from 'hooks/useModalWindow';
import {type IQualityGrading} from 'interfaces';
import {DefaultIcon, AutomaticIcon, CustomIcon} from 'styles/icons';
import UndoIcon from '@mui/icons-material/Undo';
import type {Dispatch} from 'react';
import {useTranslation} from 'react-i18next';
import {actionTypeEnum, type IFishGroup, type IFishGroupAction} from './helpers/reducer';

export const FishGroupModelAssignmentMenu = ({
  open,
  anchorEl,
  handleCloseMenu,
  state,
  dispatch,
}: {
  open: boolean;
  anchorEl: null | HTMLElement;
  state: IFishGroup;
  dispatch: Dispatch<IFishGroupAction>;
  handleCloseMenu: () => void;
}): JSX.Element => {
  const {t} = useTranslation('common');
  const {openModalWindow} = useModalWindow();

  const fishGroupQualityModelParameters = state.fishGroup?.qualityModelAttribute?.customArguments?.parameters;
  const qualityModelParameters: IQualityGrading = fishGroupQualityModelParameters
    ? {
        production: Number(fishGroupQualityModelParameters.production),
        vrak: Number(fishGroupQualityModelParameters.vrak),
        ordinary: Number(fishGroupQualityModelParameters.ordinary),
        superior: Number(fishGroupQualityModelParameters.superior),
      }
    : DEFAULT_FIXED_QUALITY_GRADING;

  const handleSetCustomModel = () => {
    openModalWindow(FishGroupQualityModal, {
      onClose: ({parameters}: {parameters: {production: number; vrak: number; ordinary: number; superior: number}}) => {
        dispatch({type: actionTypeEnum.setCustomQualityModel, parameters});
      },
      payload: {
        parameters: qualityModelParameters,
      },
    });
    handleCloseMenu();
  };

  const handleSetDefaultAssignmentModel = () => {
    dispatch({
      type: actionTypeEnum.setQualityModelAssignment,
      qualityAssignmentType: WriteQualityModelAttributeAssignmentTypeEnum.Default,
    });
    handleCloseMenu();
  };

  const handleUseAutomaticModel = () => {
    dispatch({
      type: actionTypeEnum.setQualityModelAssignment,
      qualityAssignmentType: WriteQualityModelAttributeAssignmentTypeEnum.Automatic,
    });
    handleCloseMenu();
  };

  const handleRevert = () => {
    dispatch({type: actionTypeEnum.resetQualityModel});
    handleCloseMenu();
  };

  return (
    <Menu open={open} anchorEl={anchorEl} onClose={handleCloseMenu}>
      <MenuItem onClick={handleSetCustomModel} data-testid="set-custom-quality-model">
        <ListItemIcon>
          <CustomIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          <Typography>{t('setCustomQualityModel')}</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={handleSetDefaultAssignmentModel} data-testid="set-default-quality-model">
        <ListItemIcon>
          <DefaultIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          <Typography>{t('setDefaultQualityModel')}</Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem
        onClick={handleUseAutomaticModel}
        data-testid="set-automatic-quality-model"
        disabled={disableAutomaticModel(state.fishGroups)}
      >
        <ListItemIcon>
          <AutomaticIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body2">{t('setAutomaticQualityModel')}</Typography>
        </ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleRevert} disabled={disableRevert(state.fishGroups)}>
        <ListItemIcon>
          <UndoIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body2">{t('removeOverride')}</Typography>
        </ListItemText>
      </MenuItem>
    </Menu>
  );
};

const disableAutomaticModel = (fishGroups: FishGroup[]): boolean => {
  return fishGroups?.some((fishGroup) => !fishGroup.qualityModelAttribute.automaticArguments);
};

const disableRevert = (fishGroups: FishGroup[]): boolean => {
  return !fishGroups?.some((fishGroup) => fishGroup.qualityModelAttribute.isPinned);
};
